import { useId, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { CourseApiError, isTooOften } from '../../hooks/useCourse';
import { useCourseFeedback } from '../../hooks/useCourseFeedback';
import type { FeedbackDifficulty } from '../../types/course';
import styles from './FeedbackForm.module.css';

/** Server limit of each text field (characters, after trimming). */
export const FEEDBACK_MAX = 1000;
const LEVELS: FeedbackDifficulty[] = ['easy', 'ok', 'hard'];

interface Props {
  /** The lesson the feedback is about; omitted = feedback about the whole course. */
  lessonCode?: string;
}

type FieldKey = 'liked' | 'disliked';
type FormError = { kind: 'empty' } | { kind: 'long'; field: FieldKey; count: number } | null;

/**
 * Optional feedback: «что понравилось», «что не понравилось» and «легко / нормально / сложно». Checked on submit
 * (at least one of the three, each text up to 1000 characters); the input stays on any error. Below the form the
 * learner sees their own feedback to this lesson with «Удалить».
 */
export function FeedbackForm({ lessonCode }: Props): JSX.Element {
  const { t, i18n } = useTranslation('course');
  const uid = useId();
  const { items, submit, remove, submitting, removing } = useCourseFeedback(lessonCode);
  const [difficulty, setDifficulty] = useState<FeedbackDifficulty | null>(null);
  const [liked, setLiked] = useState('');
  const [disliked, setDisliked] = useState('');
  const [formError, setFormError] = useState<FormError>(null);
  const [sendError, setSendError] = useState<unknown>(null);
  const [deleteError, setDeleteError] = useState(false);
  const [sent, setSent] = useState(false);
  const likedRef = useRef<HTMLTextAreaElement>(null);
  const dislikedRef = useRef<HTMLTextAreaElement>(null);
  const course = !lessonCode;

  function validate(): FormError {
    const l = liked.trim();
    const d = disliked.trim();
    if (l.length > FEEDBACK_MAX) return { kind: 'long', field: 'liked', count: l.length };
    if (d.length > FEEDBACK_MAX) return { kind: 'long', field: 'disliked', count: d.length };
    if (!difficulty && !l && !d) return { kind: 'empty' };
    return null;
  }

  async function handleSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (submitting) return;
    setSent(false);
    setSendError(null);
    const err = validate();
    setFormError(err);
    if (err) {
      // The first error gets the focus (an empty form: the first field).
      (err.kind === 'long' && err.field === 'disliked' ? dislikedRef : likedRef).current?.focus();
      return;
    }
    const l = liked.trim();
    const d = disliked.trim();
    try {
      await submit({ ...(difficulty ? { difficulty } : {}), ...(l ? { liked: l } : {}), ...(d ? { disliked: d } : {}) });
      setDifficulty(null);
      setLiked('');
      setDisliked('');
      setSent(true);
    } catch (e2) {
      setSendError(e2);
    }
  }

  async function handleDelete(id: string): Promise<void> {
    setDeleteError(false);
    try {
      await remove(id);
    } catch (e) {
      // 404 = already gone: the list refreshes and the item disappears; anything else is told.
      if (!(e instanceof CourseApiError && e.status === 404)) setDeleteError(true);
    }
  }

  function sendErrorText(e: unknown): string {
    if (e instanceof CourseApiError) {
      if (e.status === 429 && e.code === 'feedback_limit') return t(course ? 'feedback.limitCourse' : 'feedback.limit');
      if (isTooOften(e)) return t('lesson.tooOften');
      if (e.status === 403 && (e.code === 'locked' || e.code === 'not_launched')) return t('feedback.closed');
    }
    return t('feedback.error');
  }

  const longError = (field: FieldKey): string | null => {
    if (formError?.kind !== 'long' || formError.field !== field) return null;
    return t('feedback.tooLong', { count: formError.count, max: FEEDBACK_MAX, over: formError.count - FEEDBACK_MAX });
  };
  const likedError = longError('liked');
  const dislikedError = longError('disliked');
  const emptyError = formError?.kind === 'empty';

  const dateOf = (iso: string): string => {
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(i18n.language, { day: 'numeric', month: 'long' });
  };

  return (
    <div className={styles.root} data-testid="feedback-form">
      <h3 className={styles.title}>{t(course ? 'feedback.titleCourse' : 'feedback.titleLesson')}</h3>
      <p className={styles.hint}>{t('feedback.optional')}</p>
      <form className={styles.form} onSubmit={(e) => void handleSubmit(e)} noValidate>
        <fieldset className={styles.levels}>
          <legend className={styles.label}>{t('feedback.difficulty')}</legend>
          <div className={styles.levelRow}>
            {LEVELS.map((lv) => (
              <button
                key={lv}
                type="button"
                className={`${styles.level} ${difficulty === lv ? styles.levelOn : ''}`}
                aria-pressed={difficulty === lv}
                onClick={() => { setDifficulty(difficulty === lv ? null : lv); setSent(false); }}
                data-testid={`feedback-level-${lv}`}
              >
                {t(`feedback.level.${lv}`)}
              </button>
            ))}
          </div>
        </fieldset>

        <div className={styles.field}>
          <label className={styles.label} htmlFor={`${uid}-liked`}>{t('feedback.liked')}</label>
          <textarea
            id={`${uid}-liked`}
            ref={likedRef}
            className={`${styles.textarea} ${likedError ? styles.invalid : ''}`}
            rows={3}
            value={liked}
            aria-invalid={!!likedError || emptyError}
            aria-describedby={likedError ? `${uid}-liked-err` : emptyError ? `${uid}-empty-err` : undefined}
            onChange={(e) => { setLiked(e.target.value); setSent(false); }}
            data-testid="feedback-liked"
          />
          {likedError && <p id={`${uid}-liked-err`} role="alert" className={styles.error} data-testid="feedback-liked-error">{likedError}</p>}
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor={`${uid}-disliked`}>{t('feedback.disliked')}</label>
          <textarea
            id={`${uid}-disliked`}
            ref={dislikedRef}
            className={`${styles.textarea} ${dislikedError ? styles.invalid : ''}`}
            rows={3}
            value={disliked}
            aria-invalid={!!dislikedError}
            aria-describedby={dislikedError ? `${uid}-disliked-err` : undefined}
            onChange={(e) => { setDisliked(e.target.value); setSent(false); }}
            data-testid="feedback-disliked"
          />
          {dislikedError && <p id={`${uid}-disliked-err`} role="alert" className={styles.error} data-testid="feedback-disliked-error">{dislikedError}</p>}
        </div>

        {emptyError && <p id={`${uid}-empty-err`} role="alert" className={styles.error} data-testid="feedback-empty-error">{t('feedback.empty')}</p>}
        {sendError !== null && <p role="alert" className={styles.error} data-testid="feedback-send-error">{sendErrorText(sendError)}</p>}
        {sent && <p role="status" className={styles.sent} data-testid="feedback-sent">{t('feedback.sent')}</p>}
        <button type="submit" className={styles.submit} disabled={submitting} data-testid="feedback-submit">
          {submitting ? t('feedback.sending') : t('feedback.submit')}
        </button>
      </form>

      {items.length > 0 && (
        <section className={styles.mine} aria-labelledby={`${uid}-mine`} data-testid="feedback-list">
          <h4 className={styles.mineTitle} id={`${uid}-mine`}>{t('feedback.mine')}</h4>
          <ul className={styles.items}>
            {items.map((it) => (
              <li key={it.id} className={styles.item} data-testid={`feedback-item-${it.id}`}>
                <p className={styles.itemHead}>
                  {dateOf(it.createdAt)}
                  {it.difficulty && <> · {t(`feedback.level.${it.difficulty}`)}</>}
                </p>
                {it.liked && <p className={styles.itemText}><strong>{t('feedback.itemLiked')}</strong> {it.liked}</p>}
                {it.disliked && <p className={styles.itemText}><strong>{t('feedback.itemDisliked')}</strong> {it.disliked}</p>}
                <button
                  type="button"
                  className={styles.delete}
                  disabled={removing}
                  onClick={() => void handleDelete(it.id)}
                  data-testid={`feedback-delete-${it.id}`}
                >
                  {t('feedback.delete')}
                </button>
              </li>
            ))}
          </ul>
          {deleteError && <p role="alert" className={styles.error} data-testid="feedback-delete-error">{t('feedback.deleteError')}</p>}
        </section>
      )}
    </div>
  );
}
